import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "salt", "encryptedPrivateKeyInput", "publicKeyInput"];

  connect() {
    this.saltBuffer = this.#encode(this.salt);
  }

  async encrypt(event) {
    if (this.element.hasAttribute("data-processed")) {
      return true;
    }
    event.preventDefault();

    // Check if passphrase is valid before proceeding
    const passphraseController = this.application.getControllerForElementAndIdentifier(
      this.element.querySelector('[data-controller*="passphrase"]'), 
      'passphrase'
    );
    
    if (passphraseController && !passphraseController.isValid) {
      alert('Please ensure your passphrase meets all the requirements before submitting.');
      return false;
    }

    const secretKey = await this.deriveSecretKey();
    const { publicKey, privateKey } = await this.generateKeyPair();

    const wrappedPrivateKey = await crypto.subtle.wrapKey("jwk", privateKey, secretKey, {
      name: "AES-GCM",
      iv: this.saltBuffer,
    });

    const exportedPublicKey = await crypto.subtle.exportKey("spki", publicKey);
    const packedPublicKey = this.#pack(exportedPublicKey);
    const packedWrappedPrivateKey = this.#pack(wrappedPrivateKey);

    this.encryptedPrivateKeyInputTarget.value = packedWrappedPrivateKey;
    this.publicKeyInputTarget.value = packedPublicKey;

    this.element.setAttribute("data-processed", "true");
    this.element.submit();
    this.element.reset();
  }

  async deriveSecretKey() {
    let derivedKeyMaterial = await crypto.subtle.importKey(
      "raw",
      this.#encode(this.passphrase),
      { name: "PBKDF2" },
      false, // extractable
      ["deriveKey"],
    );

    return await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: this.saltBuffer,
        // https://cheatsheetseries.owasp.org/cheatsheets/Password_Storage_Cheat_Sheet.html#pbkdf2
        iterations: 600000,
        hash: "SHA-256",
      },
      derivedKeyMaterial,
      { name: "AES-GCM", length: 256 },
      true, // extractable
      ["wrapKey", "unwrapKey"],
    );
  }

  async generateKeyPair() {
    return crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 4096,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      true,
      ["encrypt", "decrypt"],
    );
  }

  get salt() {
    return this.saltTarget.value;
  }

  get passphrase() {
    return this.inputTarget.value;
  }

  #encode(data) {
    // https://developer.mozilla.org/en-US/docs/Web/API/TextEncoder
    const encoder = new TextEncoder();

    return encoder.encode(data);
  }

  #decode(bytestream) {
    // https://developer.mozilla.org/en-US/docs/Web/API/TextDecoder
    const decoder = new TextDecoder();

    return decoder.decode(bytestream);
  }

  #pack(buffer) {
    // https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
    return window.btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
  }
}
